import React, { MouseEvent, ReactNode, useState } from 'react';
import {
  Box,
  IconButton,
  Icons,
  Menu,
  MenuItem,
  Tooltip,
  useTheme,
} from '@ublend-npm/aulaui-core';
import { HANDIN_PROVIDER } from '@core/assignments/constants';
import DeleteSubmissionModal from './DeleteSubmissionModal';
import { createStyles } from './SubmissionActions.styles';

export type SubmissionActionItem = {
  disabled?: boolean;
  icon?: ReactNode;
  id: string;
  label: string;
  onClick: () => void;
};

type Props = {
  canDeleteSubmission: boolean;
  existingItems?: SubmissionActionItem[];
  hasSubmission: boolean;
  onDeleteError?: (error: Error) => void;
  onDeleteSubmission?: () => Promise<void> | void;
  provider: string;
  submissionId?: string;
  studentFirstName?: string;
  studentName: string;
};

const SubmissionActions = ({
  canDeleteSubmission,
  existingItems = [],
  hasSubmission,
  onDeleteError,
  onDeleteSubmission,
  provider,
  submissionId,
  studentFirstName,
  studentName,
}: Props) => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const canDelete =
    canDeleteSubmission &&
    provider === HANDIN_PROVIDER &&
    hasSubmission &&
    Boolean(onDeleteSubmission);

  if (!existingItems.length && !canDelete) return null;

  const stopRowInteraction = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const openMenu = (event: MouseEvent<HTMLElement>) => {
    stopRowInteraction(event);
    setAnchorEl(event.currentTarget);
  };

  const selectItem = (event: MouseEvent, item: SubmissionActionItem) => {
    stopRowInteraction(event);
    setAnchorEl(null);
    item.onClick();
  };

  const requestDelete = (event: MouseEvent) => {
    stopRowInteraction(event);
    setAnchorEl(null);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    if (!deleting) setDeleteModalOpen(false);
  };

  const confirmDelete = async () => {
    if (!onDeleteSubmission || deleting) return;

    setDeleting(true);
    try {
      await onDeleteSubmission();
      setDeleteModalOpen(false);
    } catch (error) {
      if (onDeleteError) {
        onDeleteError(
          error instanceof Error ? error : new Error('Submission delete failed')
        );
      }
    } finally {
      setDeleting(false);
    }
  };

  const actionId = String(submissionId || studentName)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-');
  const triggerId = `submission-options-${actionId}`;

  return (
    <>
      <Box component="span" sx={{ display: 'inline-flex' }}>
        <Tooltip title="Submission options">
          <span>
            <IconButton
              aria-label={`Submission options for ${studentName}`}
              id={triggerId}
              onClick={openMenu}
              size="small"
              sx={styles.trigger}
              type="button"
            >
              <Icons.MoreVert fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        aria-labelledby={triggerId}
        onClick={(event) => event.stopPropagation()}
        onClose={() => setAnchorEl(null)}
        open={Boolean(anchorEl)}
        sx={styles.menu}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {existingItems.map((item) => (
          <MenuItem
            data-testid={`submission-action-${item.id}`}
            disabled={item.disabled}
            icon={item.icon}
            key={item.id}
            label={item.label}
            onClick={(event) => selectItem(event, item)}
          />
        ))}
        {canDelete && (
          <MenuItem
            data-testid="submission-action-delete"
            icon={<Icons.DeleteOutlined fontSize="small" />}
            label="Delete Submission"
            onClick={requestDelete}
          />
        )}
      </Menu>

      {canDelete && (
        <DeleteSubmissionModal
          deleting={deleting}
          id={`delete-submission-${actionId}`}
          onClose={closeDeleteModal}
          onConfirm={confirmDelete}
          open={deleteModalOpen}
          studentName={studentFirstName || studentName}
        />
      )}
    </>
  );
};

export default SubmissionActions;
